import type {
  PdfApiErrorResponse,
  PdfApiRequestPayload,
} from "./api-contract";
import { sanitizePdfFilename } from "./filename";

export type PdfDownload = {
  blob: Blob;
  filename: string;
};

type PdfDownloadOptions = {
  timeoutMs?: number;
};

export class PdfDownloadError extends Error {
  readonly retryAfterSeconds: number | null;

  constructor(message: string, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = "PdfDownloadError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export const defaultPdfDownloadRequestTimeoutMs = 60_000;

const encodedFilenamePattern = /filename\*=UTF-8''([^;]+)/i;
const quotedFilenamePattern = /filename="([^"]+)"/i;
const bareFilenamePattern = /filename=([^;]+)/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPdfApiErrorResponse(value: unknown): value is PdfApiErrorResponse {
  return (
    isRecord(value) &&
    isRecord(value.error) &&
    typeof value.error.message === "string"
  );
}

function decodeHeaderFilename(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function getPdfDownloadFilename(
  contentDisposition: string | null,
  fallbackFilename: string,
) {
  const encodedFilename = contentDisposition?.match(encodedFilenamePattern)?.[1];

  if (encodedFilename) {
    return sanitizePdfFilename(decodeHeaderFilename(encodedFilename));
  }

  const quotedFilename = contentDisposition?.match(quotedFilenamePattern)?.[1];

  if (quotedFilename) {
    return sanitizePdfFilename(quotedFilename);
  }

  const bareFilename = contentDisposition?.match(bareFilenamePattern)?.[1];

  if (bareFilename) {
    return sanitizePdfFilename(bareFilename.trim());
  }

  return sanitizePdfFilename(fallbackFilename);
}

export function parseRetryAfterSeconds(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const seconds = Number(value.trim());

  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  return Math.ceil(seconds);
}

async function getPdfApiErrorMessage(response: Response) {
  try {
    const body: unknown = await response.json();

    if (isPdfApiErrorResponse(body)) {
      return body.error.message;
    }
  } catch {
    return `PDF download failed with status ${response.status}.`;
  }

  return `PDF download failed with status ${response.status}.`;
}

export async function requestPdfDownload(
  payload: PdfApiRequestPayload,
  fetchPdf: typeof fetch = fetch,
  { timeoutMs = defaultPdfDownloadRequestTimeoutMs }: PdfDownloadOptions = {},
): Promise<PdfDownload> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  let response: Response;

  try {
    response = await fetchPdf("/api/pdf", {
      method: "POST",
      headers: {
        Accept: "application/pdf, application/json;q=0.9",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (error: unknown) {
    if (
      controller.signal.aborted ||
      (error instanceof DOMException && error.name === "AbortError")
    ) {
      throw new PdfDownloadError(
        "PDF generation timed out. Try a smaller document or retry.",
      );
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const retryAfterSeconds = parseRetryAfterSeconds(
      response.headers.get("Retry-After"),
    );
    throw new PdfDownloadError(
      await getPdfApiErrorMessage(response),
      retryAfterSeconds,
    );
  }

  const blob = await response.blob();

  return {
    blob,
    filename: getPdfDownloadFilename(
      response.headers.get("Content-Disposition"),
      payload.filename,
    ),
  };
}
